import {
  Card,
  CardAction,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { BundleCrumbs } from "../-crumbs";
import { createFileRoute, Link, useNavigate } from "@tanstack/react-router";
import { useSuspenseQuery } from "@tanstack/react-query";
import { suspenseOptions } from "@/lib/suspense-query";
import {
  getGetBundleQueryOptions,
  useDeleteBundle,
  useRestoreBundle,
} from "@/requests/gen/react-query/bundle";
import formatUSD from "@/lib/format-currency";
import { DiscountTypeEnum } from "@/requests/gen/react-query/fastAPI.schemas";
import { bundleLineFinalPrice } from "@/lib/discount";
import { resolveProductImage } from "@/lib/default-image";
import { Button } from "@/components/ui/button";
import { Archive, ArchiveRestore } from "lucide-react";
import { toast } from "sonner";
import { showAlert } from "@/lib/show-alert";
import numericId from "@/lib/numeric-id";

export const Route = createFileRoute(
  "/_private/_patient-360/bundle/$bundleId/",
)({
  loader: ({ context: { queryClient }, params: { bundleId } }) =>
    queryClient.ensureQueryData(getGetBundleQueryOptions(numericId(bundleId))),
  component: BundleDetailsPage,
});

function BundleDetailsPage() {
  const { bundleId } = Route.useParams();
  const { data: bundle } = useSuspenseQuery(
    suspenseOptions(getGetBundleQueryOptions(numericId(bundleId))),
  );
  const navigate = useNavigate();
  const deleteMutation = useDeleteBundle();
  const restoreMutation = useRestoreBundle();
  const isArchived = !!bundle?.is_deleted;

  const bundleTotals = bundle?.bundle_products?.reduce(
    (totals, item) => ({
      basePrice: totals.basePrice + item.product.entity_price * item.quantity,
      finalPrice:
        totals.finalPrice +
        bundleLineFinalPrice(
          item.product.entity_price,
          item.quantity,
          item.discount_type,
          item.discount_value,
        ),
    }),
    { basePrice: 0, finalPrice: 0 },
  ) ?? { basePrice: 0, finalPrice: 0 };
  const totalDiscount = bundleTotals.basePrice - bundleTotals.finalPrice;

  const showArchiveAlert = () => {
    if (!bundle) return;

    showAlert({
      title: "Archive Bundle",
      text: `Are you sure you want to archive "${bundle.name}"?`,
      img: "delete",
      cancelText: "Cancel",
      confirmText: "Archive",
      onConfirm: () => {
        if (!bundle) return;

        deleteMutation.mutate(
          { bundleId: bundle.bundle_id },
          {
            onSuccess: () => {
              toast.success("Bundle archived successfully.");
              navigate({ to: "/bundle/lookup" });
            },
            onError: (error) => {
              toast.error("Error", {
                description:
                  error?.response?.data?.detail?.toString() ||
                  "Failed to archive bundle. Please try again.",
              });
            },
          },
        );
      },
    });
  };

  const showRestoreAlert = () => {
    if (!bundle) return;

    showAlert({
      title: "Restore Bundle",
      text: `Are you sure you want to restore "${bundle.name}"?`,
      img: "delete",
      cancelText: "Cancel",
      confirmText: "Restore",
      onConfirm: () => {
        if (!bundle) return;

        restoreMutation.mutate(
          { bundleId: bundle.bundle_id },
          {
            onSuccess: () => {
              toast.success("Bundle restored successfully.");
              navigate({ to: "/bundle/lookup" });
            },
            onError: (error) => {
              toast.error("Error", {
                description:
                  error?.response?.data?.detail?.toString() ||
                  "Failed to restore bundle. Please try again.",
              });
            },
          },
        );
      },
    });
  };

  return (
    <div>
      <title>{`${bundle?.name || "Bundle"} - Legrande Health Portal`}</title>
      <BundleCrumbs />
      <div className="space-y-6">
        <Card>
          <CardHeader>
            <CardTitle
              className={`${bundle?.is_deleted ? "text-muted-foreground" : ""}`}
            >
              {bundle?.name}
              {bundle?.is_deleted && " - Bundle Deleted"}
            </CardTitle>
            <CardDescription>{bundle?.entity.name}</CardDescription>
            <CardAction className="flex items-center gap-2">
              {bundle?.previous_bundle_id && (
                <Button asChild variant="outline" size="sm">
                  <Link
                    to="/bundle/$bundleId"
                    params={{
                      bundleId: String(bundle.previous_bundle_id),
                    }}
                  >
                    Previous Bundle Version
                  </Link>
                </Button>
              )}
              {!isArchived ? (
                <>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={showArchiveAlert}
                  >
                    <Archive />
                    Archive
                  </Button>
                  <Button variant="outline" size="sm" asChild>
                    <Link
                      to="/bundle/$bundleId/products"
                      params={{ bundleId: String(bundle?.bundle_id) }}
                    >
                      Edit
                    </Link>
                  </Button>
                </>
              ) : (
                <Button variant="outline" size="sm" onClick={showRestoreAlert}>
                  <ArchiveRestore />
                  Restore
                </Button>
              )}
            </CardAction>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {bundle?.bundle_products?.map((item) => (
                <Card key={item.product_id} className="h-full flex flex-col">
                  <CardHeader className="flex-1">
                    <img
                      src={resolveProductImage(item.product.catalog_product)}
                      alt={item.product.display_name}
                      className="h-32 w-full object-contain mb-4 rounded-md"
                    />
                    <CardTitle className="text-lg">
                      <Link
                        to="/product/$productId"
                        params={{ productId: String(item.product_id) }}
                        className="hover:underline text-primary"
                      >
                        {item.product.display_name}
                      </Link>
                    </CardTitle>
                  </CardHeader>
                  <CardContent className="mt-auto">
                    <div className="space-y-2 text-sm">
                      <div className="flex justify-between">
                        <span className="text-muted-foreground">
                          Dispenser:
                        </span>
                        <span>{item.product.entity.name}</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-muted-foreground">
                          Fulfiller:
                        </span>
                        <span>
                          {item.fulfilling_pharmacy_id == null ||
                          item.fulfilling_pharmacy_id === item.product.entity_id
                            ? item.product.entity.name
                            : (item.product.fulfillers?.find(
                                (f) =>
                                  f.entity_id === item.fulfilling_pharmacy_id,
                              )?.entity.name ?? item.product.entity.name)}
                        </span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-muted-foreground">Quantity:</span>
                        <span>{item.quantity}</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-muted-foreground">
                          Base Price:
                        </span>
                        <span>{formatUSD(item.product.entity_price)}</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-muted-foreground">Type:</span>
                        <span>{item.discount_type}</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-muted-foreground">Discount:</span>
                        <span>
                          {item.discount_type === DiscountTypeEnum.Percentage
                            ? `${item.discount_value}%`
                            : formatUSD(item.discount_value)}
                        </span>
                      </div>
                      <div className="flex justify-between font-bold pt-2 border-t">
                        <span>Final Price:</span>
                        <span>
                          {formatUSD(
                            bundleLineFinalPrice(
                              item.product.entity_price,
                              item.quantity,
                              item.discount_type,
                              item.discount_value,
                            ),
                          )}
                        </span>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              ))}
              {(!bundle?.bundle_products ||
                bundle?.bundle_products.length === 0) && (
                <div className="col-span-full text-center text-muted-foreground py-8">
                  No products in this bundle.
                </div>
              )}
            </div>
            <div className="flex justify-end gap-3 mt-4">
              <div className="p-3">
                <p className="text-xs text-muted-foreground uppercase tracking-wide">
                  Total Price
                </p>
                <p className="text-lg font-semibold">
                  {formatUSD(bundleTotals.basePrice)}
                </p>
              </div>
              <div className="p-3">
                <p className="text-xs text-muted-foreground uppercase tracking-wide">
                  Total Discount
                </p>
                <p className="text-lg font-semibold">
                  {formatUSD(totalDiscount)}
                </p>
              </div>
              <div className="p-3">
                <p className="text-xs text-muted-foreground uppercase tracking-wide">
                  Discounted Price
                </p>
                <p className="text-lg font-semibold">
                  {formatUSD(bundleTotals.finalPrice)}
                </p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
